/**
 * THE HOME TIMELINE ITEM ARRAY — ORDER IS LOAD-BEARING.
 *
 * This list reproduces the original deploy/public/json/home.json "items" array
 * EXACTLY, element for element, in the original order. TimelineItemFactory
 * walks it top to bottom on each creation pass, so array position controls
 * item creation order and therefore sprite z-order within the two item passes
 * (pass 1 mounts below the WebGL layer, `pass: 2` items above it).
 *
 * The department files (intro/screens/spaces/systems) group the items for
 * humans; THIS file is the renderer's truth. When adding or moving an item,
 * its position HERE is what changes the visual stacking — keep the department
 * files as the place where the item is defined and documented, and splice the
 * export into this array at the right position.
 *
 * Parity with the retired home.json is proven by a deep-equal script (see the
 * conversion notes in the repo history).
 */
import type { HomeItemConfig } from './homeItems.types';

import { introP1, introP2, introP3, introCopy } from './intro';
import {
	screenFrames,
	screensBgBlob1,
	screensBgBlob2,
	screensBgBlob3,
	screensBgBlobGlow1,
	screensBgBlobGlow2,
	screensBgBlobGlow3,
	systemsGridScreens1,
	systemsGridScreens2,
	systemsGridScreens3,
	screensTvTopLeft,
	screensTvTopRight,
	screensTvBottomLeft,
	screensTvBottomRight,
	screenCat,
	systemsBlock1,
	systemsBlock2,
	systemsBlockMove,
	systemsBlock3,
	systemsBlock4,
	systemsBlock5,
	systemsBlock6,
	systemsBlock7,
	systemsBlock8,
	systemsBlock9,
	systemsBlock10
} from './screens';
import {
	largeScreenP,
	largeScreenP2,
	largeScreenP3,
	floorHighlight,
	spacesP1,
	spacesP2,
	spacesP3,
	spacesP4,
	spacesP5,
	spacesP6,
	spacesP7,
	spacesP8,
	spacesP9,
	spacesP10,
	spacesP11,
	spacesP12,
	spacesP13,
	spacesP14,
	spacesP15,
	spacesP16,
	spacesP17,
	spacesP18,
	spacesP19,
	spacesP20,
	spacesP21,
	spacesBox,
	amyBase,
	amyHighlight,
	personBase,
	personHighlight
} from './spaces';
import {
	systemsGrid,
	systemsSquareLeft,
	systemsP1,
	systemsP2,
	systemsP4,
	systemsP5,
	systemsP6
} from './systems';

//prettier-ignore
export const HOME_TIMELINE_ITEMS: HomeItemConfig[] = [
	largeScreenP, //0: id 'largescreenp' (spaces)
	largeScreenP2, //1: id 'largescreenp2' (spaces)
	largeScreenP3, //2: id 'largescreenp3' (spaces)
	floorHighlight, //3: id 'floorhighligt' (spaces)
	introP1, //4: id 'introp1' (home)
	introP2, //5: id 'introp2' (no zone)
	introP3, //6: id 'introp3' (home)
	screenFrames, //9: id 'screen-frames' (screens)
	screensBgBlob1, //10: id 'screens-bg-blob' (screens)
	screensBgBlob2, //11: id 'screens-bg-blob' (screens)
	screensBgBlob3, //12: id 'screens-bg-blob' (screens)
	screensBgBlobGlow1, //13: id 'screens-bg-blob-glow' (screens)
	screensBgBlobGlow2, //14: id 'screens-bg-blob-glow' (screens)
	screensBgBlobGlow3, //15: id 'screens-bg-blob-glow' (screens)
	systemsGridScreens1, //16: id 'systems-grid-screens1' (screens)
	systemsGridScreens2, //17: id 'systems-grid-screens2' (screens)
	systemsGridScreens3, //18: id 'systems-grid-screens3' (screens)
	screensTvTopLeft, //19: id 'screensTVTopLeft' (screens)
	screensTvTopRight, //20: id 'screensTVTopRight' (screens)
	screensTvBottomLeft, //21: id 'screensTVBottomLeft' (screens)
	screensTvBottomRight, //22: id 'screensTVBottomRight' (screens)
	screenCat, //23: id 'screen-cat' (screens)
	spacesP1, //25: id 'spacesp' (spaces)
	spacesP2, //26: id 'spacesp' (spaces)
	spacesP3, //27: id 'spacesp' (spaces)
	spacesP4, //28: id 'spacesp' (spaces)
	spacesP5, //29: id 'spacesp' (spaces)
	spacesP6, //30: id 'spacesp' (spaces)
	spacesP7, //31: id 'spacesp' (spaces)
	spacesP8, //32: id 'spacesp' (spaces)
	spacesP9, //33: id 'spacesp' (spaces)
	spacesP10, //34: id 'spacesp' (spaces)
	spacesP11, //35: id 'spacesp' (spaces)
	spacesP12, //36: id 'spacesp' (spaces)
	spacesP13, //37: id 'spacesp' (spaces)
	spacesP14, //38: id 'spacesp' (spaces)
	spacesP15, //39: id 'spacesp' (spaces)
	spacesP16, //40: id 'spacesp' (spaces)
	spacesP17, //41: id 'spacesp' (spaces)
	spacesP18, //42: id 'spacesp' (spaces)
	spacesP19, //43: id 'spacesp' (spaces)
	spacesP20, //44: id 'spacesp' (spaces)
	spacesP21, //45: id 'spacesp' (spaces)
	spacesBox, //46: id 'box' (spaces)
	amyBase, //47: id 'amy-base' (spaces)
	amyHighlight, //48: id 'amy-highlight' (spaces)
	personBase, //49: id 'personbase' (spaces)
	personHighlight, //50: id 'person' (spaces)
	introCopy, //51: id 'intro' (no zone)
	systemsBlock1, //52: id 'systems-block' (screens)
	systemsBlock2, //53: id 'systems-block' (screens)
	systemsBlockMove, //54: id 'systems-block-move' (screens)
	systemsBlock3, //55: id 'systems-block' (screens)
	systemsBlock4, //56: id 'systems-block' (screens)
	systemsBlock5, //57: id 'systems-block' (screens)
	systemsBlock6, //58: id 'systems-block' (screens)
	systemsBlock7, //59: id 'systems-block' (screens)
	systemsBlock8, //60: id 'systems-block' (screens)
	systemsBlock9, //61: id 'systems-block' (screens)
	systemsBlock10, //62: id 'systems-block' (screens)
	systemsGrid, //63: id 'systems-grid' (systems)
	systemsSquareLeft, //64: id 'spacesp' (systems)
	systemsP1, //65: id 'systemsp1' (systems)
	systemsP2, //66: id 'systemsp2' (systems)
	systemsP4, //67: id 'systemsp4' (systems)
	systemsP5, //68: id 'systemsp5' (systems)
	systemsP6, //69: id 'systemsp6' (systems)
];

export type { HomeItemConfig } from './homeItems.types';
